import { ZipFileSystem, ZipReadFileSystem } from '@playcanvas/splat-transform';
import { Quat, Vec3 } from 'playcanvas';

import { ElementType } from './element';
import { Events } from './events';
import { BrowserFileSystem, BlobReadSource } from './io';
import { Mesh } from './mesh';
import { recentFiles } from './recent-files';
import { Scene } from './scene';
import { Splat } from './splat';
import { serializePly } from './splat-serialize';
import { Transform } from './transform';
import { localize } from './ui/localization';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

const SuperFileType: FilePickerAcceptType[] = [{
    description: 'NSiteAI document',
    accept: {
        'application/x-supersplat': ['.nsproj', '.ssproj']
    }
}];

type FileSelectorCallback = (fileList: File) => void;

type MeshDocumentState = ReturnType<Mesh['docSerialize']>;

type MeshDocumentMedia = {
    path: string;
    name?: string;
};

type MeshDocumentEntry = MeshDocumentState & {
    asset: {
        path: string;
        name?: string;
    };
    texture?: MeshDocumentMedia;
    video?: MeshDocumentMedia;
};

const sanitizeFilename = (name: string) => {
    const trimmed = (name || '').trim();
    const safe = trimmed.replace(/[<>:"|?*\x00-\x1f]/g, '_');
    return safe.length ? safe : 'asset';
};

const basename = (path: string) => {
    const normalized = (path || '').replace(/\\/g, '/');
    return normalized.split('/').pop() || normalized || 'asset';
};

const normalizeArchiveRelativePath = (path: string) => {
    const normalized = (path || '').replace(/\\/g, '/').split('#')[0].split('?')[0];
    const parts = normalized.split('/');
    const result: string[] = [];

    for (const part of parts) {
        if (!part || part === '.') {
            continue;
        }

        if (part === '..') {
            result.pop();
            continue;
        }

        result.push(part);
    }

    return result.join('/');
};

const getMimeTypeFromPath = (path: string, fallback = 'application/octet-stream') => {
    const lower = path.toLowerCase();
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    if (lower.endsWith('.webp')) return 'image/webp';
    if (lower.endsWith('.gif')) return 'image/gif';
    if (lower.endsWith('.bmp')) return 'image/bmp';
    if (lower.endsWith('.svg')) return 'image/svg+xml';
    if (lower.endsWith('.webm')) return 'video/webm';
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.mov')) return 'video/quicktime';
    if (lower.endsWith('.gltf')) return 'model/gltf+json';
    if (lower.endsWith('.glb')) return 'model/gltf-binary';
    if (lower.endsWith('.obj')) return 'text/plain';
    if (lower.endsWith('.bin')) return 'application/octet-stream';
    return fallback;
};

const writeZipBlob = async (zipFs: ZipFileSystem, filename: string, blob: Blob) => {
    const writer = await zipFs.createWriter(filename);
    await writer.write(new Uint8Array(await blob.arrayBuffer()));
    await writer.close();
};

const readZipBlob = async (zipFs: ZipReadFileSystem, filename: string, mimeType?: string) => {
    try {
        const source = await zipFs.createSource(filename);
        const data = await source.read().readAll();
        source.close();
        return new Blob([data.slice().buffer], { type: mimeType || getMimeTypeFromPath(filename) });
    } catch (error) {
        return null;
    }
};

const readZipFile = async (zipFs: ZipReadFileSystem, filename: string, nameHint?: string) => {
    const blob = await readZipBlob(zipFs, filename, getMimeTypeFromPath(filename));
    if (!blob) {
        return null;
    }

    return new File([blob], nameHint || basename(filename), { type: blob.type || getMimeTypeFromPath(filename) });
};

const getMeshSideResources = (mesh: Mesh) => {
    const resources = (mesh as any)._sourceMeshResources;
    if (resources instanceof Map) {
        return Array.from(resources.entries()) as Array<[string, Blob]>;
    }

    if (resources && typeof resources === 'object') {
        return Object.entries(resources).filter((entry): entry is [string, Blob] => entry[1] instanceof Blob);
    }

    return [] as Array<[string, Blob]>;
};

const getExternalGltfDependencies = async (blob: Blob) => {
    try {
        const gltf = JSON.parse(await blob.text());
        const dependencies = new Set<string>();
        const collectUri = (uri: unknown) => {
            if (typeof uri !== 'string' || !uri || uri.startsWith('data:') || uri.startsWith('blob:') || /^(?:[a-z]+:)?\/\//i.test(uri)) {
                return;
            }

            const normalized = normalizeArchiveRelativePath(uri);
            if (normalized) {
                dependencies.add(normalized);
            }
        };

        if (Array.isArray(gltf?.buffers)) {
            gltf.buffers.forEach((buffer: any) => collectUri(buffer?.uri));
        }

        if (Array.isArray(gltf?.images)) {
            gltf.images.forEach((image: any) => collectUri(image?.uri));
        }

        return Array.from(dependencies.values());
    } catch (error) {
        throw new Error('Failed to inspect GLTF side resources for document packaging');
    }
};

const packageMeshForDocument = async (mesh: Mesh, meshIndex: number, zipFs: ZipFileSystem): Promise<MeshDocumentEntry> => {
    const basePath = `meshes/mesh_${meshIndex}`;
    const meshAny = mesh as any;
    const assetFile = (mesh.asset as any)?.file as { url?: string; filename?: string } | undefined;
    const sourceNameHint = mesh.sourceMeshName || assetFile?.filename || mesh.filename || `mesh_${meshIndex}.glb`;
    const meshBlob = mesh.sourceMeshFile ?? (assetFile?.url ? await (async () => {
        const response = await fetch(assetFile.url);
        if (!response.ok) {
            throw new Error(`Failed to fetch mesh source '${assetFile.url}'`);
        }
        return new Blob([await response.arrayBuffer()], { type: getMimeTypeFromPath(sourceNameHint) });
    })() : null);

    if (!meshBlob) {
        throw new Error(`Mesh '${mesh.name}' cannot be saved because its source asset is unavailable`);
    }

    const sideResources = getMeshSideResources(mesh);
    if (sourceNameHint.toLowerCase().endsWith('.gltf')) {
        const requiredDependencies = await getExternalGltfDependencies(meshBlob);
        if (requiredDependencies.length > 0) {
            const availableDependencies = new Set(sideResources.map(([resourcePath]) => normalizeArchiveRelativePath(resourcePath)).filter(Boolean));
            const missingDependency = requiredDependencies.find(path => !availableDependencies.has(path));
            if (missingDependency) {
                throw new Error(`Mesh '${mesh.name}' cannot be saved because GLTF dependency '${missingDependency}' is unavailable for rebundling`);
            }
        }
    }

    const meshArchiveName = sanitizeFilename(basename(sourceNameHint));
    const meshArchivePath = `${basePath}/${meshArchiveName}`;
    await writeZipBlob(zipFs, meshArchivePath, meshBlob);

    for (const [resourcePath, resourceBlob] of sideResources) {
        const normalizedResourcePath = normalizeArchiveRelativePath(resourcePath);
        if (!normalizedResourcePath) {
            continue;
        }

        await writeZipBlob(zipFs, `${basePath}/${normalizedResourcePath}`, resourceBlob);
    }

    const entry: MeshDocumentEntry = {
        ...mesh.docSerialize(),
        asset: {
            path: meshArchivePath,
            name: meshArchiveName
        }
    };

    const textureFile = meshAny._sourceTextureFile as File | Blob | null | undefined;
    const textureName = (meshAny._sourceTextureName as string | undefined) || (textureFile instanceof File ? textureFile.name : undefined);
    if (textureFile instanceof Blob) {
        const textureArchiveName = sanitizeFilename(textureName || `mesh_${meshIndex}_diffuse.png`);
        const textureArchivePath = `${basePath}/textures/${textureArchiveName}`;
        await writeZipBlob(zipFs, textureArchivePath, textureFile);
        entry.texture = {
            path: textureArchivePath,
            name: textureArchiveName
        };
    }

    const videoFile = meshAny._sourceVideoFile as File | null | undefined;
    if (videoFile instanceof File) {
        const videoArchiveName = sanitizeFilename(videoFile.name || `mesh_${meshIndex}_video.webm`);
        const videoArchivePath = `${basePath}/video/${videoArchiveName}`;
        await writeZipBlob(zipFs, videoArchivePath, videoFile);
        entry.video = {
            path: videoArchivePath,
            name: videoArchiveName
        };
    }

    return entry;
};

const restoreMeshMediaFromDocument = async (mesh: Mesh, doc: MeshDocumentEntry, zipFs: ZipReadFileSystem) => {
    if (doc.video?.path) {
        const videoFile = await readZipFile(zipFs, doc.video.path, doc.video.name);
        if (videoFile) {
            await mesh.loadVideoTexture(videoFile);
        }
    } else if (doc.texture?.path) {
        const textureFile = await readZipFile(zipFs, doc.texture.path, doc.texture.name);
        if (textureFile) {
            await mesh.loadTexture(textureFile);
        }
    }

    if (doc.hasOwnProperty('useAlpha')) {
        mesh.useAlpha = !!doc.useAlpha;
    }
    if (doc.hasOwnProperty('videoPlaybackMode')) {
        mesh.videoPlaybackMode = doc.videoPlaybackMode === 'realtime' ? 'realtime' : 'per-frame';
    }
    if (doc.hasOwnProperty('billboard')) {
        mesh.billboard = !!doc.billboard;
    }
    if (doc.hasOwnProperty('shadowCatcher')) {
        mesh.shadowCatcher = !!doc.shadowCatcher;
    }
};

// helper class to show a file selector dialog.
// used when showOpenFilePicker is not available.
class FileSelector {
    show: (callbackFunc: FileSelectorCallback) => void;

    constructor() {
        const fileSelector = document.createElement('input');
        fileSelector.setAttribute('id', 'document-file-selector');
        fileSelector.setAttribute('type', 'file');
        fileSelector.setAttribute('accept', '.nsproj,.ssproj');
        fileSelector.setAttribute('multiple', 'false');

        document.body.append(fileSelector);

        let callbackFunc: FileSelectorCallback = null;

        fileSelector.addEventListener('change', () => {
            callbackFunc(fileSelector.files[0]);
        });

        fileSelector.addEventListener('cancel', () => {
            callbackFunc(null);
        });

        this.show = (func: FileSelectorCallback) => {
            callbackFunc = func;
            fileSelector.click();
        };
    }
}

const registerDocEvents = (scene: Scene, events: Events) => {
    // construct the file selector
    const fileSelector = window.showOpenFilePicker ? null : new FileSelector();

    // this file handle is updated as the current document is loaded and saved
    let documentFileHandle: FileSystemFileHandle = null;

    // Guard against concurrent loads (can happen via repeated open actions while a slow ZIP is loading).
    let loadInProgress = false;

    // show the user a reset confirmation popup
    const getResetConfirmation = async () => {
        const result = await events.invoke('showPopup', {
            type: 'yesno',
            header: localize('doc.reset'),
            message: localize(events.invoke('scene.dirty') ? 'doc.unsaved-message' : 'doc.reset-message')
        });

        if (result.action !== 'yes') {
            return false;
        }

        return true;
    };

    // reset the scene
    const resetScene = () => {
        events.fire('scene.clear');
        events.fire('camera.reset');
        events.fire('doc.setName', null);
        documentFileHandle = null;
    };

    // load the document from the given file
    const loadDocument = async (file: File): Promise<boolean> => {
        events.fire('startSpinner');

        // Create streaming ZIP reader from the file
        const blobSource = new BlobReadSource(file);
        const zipFs = new ZipReadFileSystem(blobSource);

        let ok = false;

        try {
            // reset the scene
            resetScene();

            // read document.json via streaming (only reads what's needed)
            const docSource = await zipFs.createSource('document.json');
            const docData = await docSource.read().readAll();
            docSource.close();
            const document = JSON.parse(new TextDecoder().decode(docData));

            // Validate splat count to prevent corrupted documents from exhausting memory
            const MAX_SPLATS = 1000; // Reasonable limit for a single document
            if (!Array.isArray(document.splats)) {
                throw new Error('Invalid document: splats must be an array');
            }
            if (document.splats.length > MAX_SPLATS) {
                throw new Error(`Document contains too many splats: ${document.splats.length} (max ${MAX_SPLATS})`);
            }
            if (document.hasOwnProperty('meshes') && !Array.isArray(document.meshes)) {
                throw new Error('Invalid document: meshes must be an array');
            }

            // run through each splat and load it
            for (let i = 0; i < document.splats.length; ++i) {
                const filename = `splat_${i}.ply`;
                const splatSettings = document.splats[i];

                // load splat directly from the zip filesystem (streams on-demand)
                // skipReorder=true because ssproj PLY files are already in morton order
                const splat = await scene.assetLoader.load(filename, zipFs, false, true);

                await scene.add(splat);

                splat.docDeserialize(splatSettings);
            }

            const meshEntries = (document.meshes ?? []) as MeshDocumentEntry[];
            for (const meshEntry of meshEntries) {
                if (!meshEntry?.asset?.path) {
                    throw new Error('Invalid document: mesh asset path is missing');
                }

                const mesh = await scene.assetLoader.load(meshEntry.asset.path, zipFs) as Mesh;
                await scene.add(mesh);
                mesh.docDeserialize(meshEntry);
                await restoreMeshMediaFromDocument(mesh, meshEntry, zipFs);
            }

            // FIXME: trigger scene bound calc in a better way
            const tmp = scene.bound;
            if (tmp === null) {
                console.error('this should never fire');
            }

            events.invoke('docDeserialize.timeline', document.timeline);
            events.invoke('docDeserialize.poseSets', document.poseSets, document.camera?.fov);
            events.invoke('docDeserialize.view', document.view);
            scene.camera.docDeserialize(document.camera);

            // Optional: baked vehicle motion helpers/controllers.
            await events.invoke('docDeserialize.vehicleMotion', document.vehicleMotion);

            // Optional: baked camera motion helpers/controllers.
            await events.invoke('docDeserialize.cameraMotion', document.cameraMotion);

            // Optional: user-created scene cameras.
            await events.invoke('docDeserialize.sceneCameras', (document as any).sceneCameras);

            // refresh the pivot to reflect the loaded transform
            const currentSelection = events.invoke('selection') as any;
            if (currentSelection) {
                const pivot = events.invoke('pivot') as any;
                const next = new Transform();

                // splats expose a getPivot helper (center / boundCenter)
                if (currentSelection?.type === ElementType.splat && typeof currentSelection.getPivot === 'function') {
                    const pivotOrigin = events.invoke('pivot.origin');
                    currentSelection.getPivot(pivotOrigin === 'center' ? 'center' : 'boundCenter', false, next);
                    pivot.place(next);
                } else {
                    // other selectable elements: infer TRS from either a `pivot`, `entity`, or the selection itself.
                    const pos = currentSelection?.pivot?.getLocalPosition?.() ??
                        currentSelection?.entity?.getLocalPosition?.() ??
                        currentSelection?.getLocalPosition?.();
                    const rot = currentSelection?.pivot?.getLocalRotation?.() ??
                        currentSelection?.entity?.getLocalRotation?.() ??
                        currentSelection?.getLocalRotation?.();
                    const scl = currentSelection?.pivot?.getLocalScale?.() ??
                        currentSelection?.entity?.getLocalScale?.() ??
                        currentSelection?.getLocalScale?.();

                    next.set(
                        pos ?? new Vec3(),
                        rot ?? new Quat(),
                        scl ?? new Vec3(1, 1, 1)
                    );
                    pivot.place(next);
                }
            }

            ok = true;
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('doc.load-failed'),
                message: `'${error.message ?? error}'`
            });
        } finally {
            // Clean up resources
            zipFs.close();
            events.fire('stopSpinner');
        }

        return ok;
    };

    const loadDocumentGuarded = async (file: File): Promise<boolean> => {
        if (loadInProgress) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('doc.load-failed'),
                message: 'A document is already loading. Please wait for it to finish.'
            });
            return false;
        }

        loadInProgress = true;
        try {
            return await loadDocument(file);
        } finally {
            loadInProgress = false;
        }
    };

    const saveDocument = async (options: { stream?: FileSystemWritableFileStream, filename?: string }) => {
        events.fire('startSpinner');

        try {
            const splats = events.invoke('scene.allSplats') as Splat[];
            const meshes = scene.getElementsByType(ElementType.model) as Mesh[];

            const serializeSettings = {
                // even though we support saving selection state, we disable that for now
                // because including a uint8 array in the document PLY results in slow loading
                // path.
                keepStateData: false,
                keepWorldTransform: true,
                keepColorTint: true
            };

            // Create browser filesystem and zip filesystem
            const browserFs = new BrowserFileSystem(options.filename, options.stream);
            const browserWriter = await browserFs.createWriter(options.filename);
            const zipFs = new ZipFileSystem(browserWriter);

            // Write each splat as PLY
            for (let i = 0; i < splats.length; ++i) {
                await serializePly([splats[i]], serializeSettings, zipFs, `splat_${i}.ply`);
            }

            const meshEntries: MeshDocumentEntry[] = [];
            for (let i = 0; i < meshes.length; ++i) {
                meshEntries.push(await packageMeshForDocument(meshes[i], i, zipFs));
            }

            const document = {
                version: 0,
                camera: scene.camera.docSerialize(),
                view: events.invoke('docSerialize.view'),
                poseSets: events.invoke('docSerialize.poseSets'),
                timeline: events.invoke('docSerialize.timeline'),
                cameraMotion: events.invoke('docSerialize.cameraMotion'),
                vehicleMotion: events.invoke('docSerialize.vehicleMotion'),
                sceneCameras: events.invoke('docSerialize.sceneCameras'),
                splats: splats.map(s => s.docSerialize()),
                meshes: meshEntries
            };

            // Write document.json after packaging so asset references are finalized.
            const docWriter = await zipFs.createWriter('document.json');
            await docWriter.write(new TextEncoder().encode(JSON.stringify(document)));
            await docWriter.close();

            // Close zip (also closes underlying browser writer)
            await zipFs.close();
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: localize('doc.save-failed'),
                message: `'${error.message ?? error}'`
            });
        } finally {
            events.fire('stopSpinner');
        }
    };

    // handle user requesting a new document
    events.function('doc.new', async () => {
        if (!await getResetConfirmation()) {
            return false;
        }
        resetScene();
        return true;
    });

    // handle document file being dropped
    // NOTE: on chrome it's possible to get the FileSystemFileHandle from the DataTransferItem
    // (which would result in more seamless user experience), but this is not yet supported in
    // other browsers.
    events.function('doc.load', async (file: File, handle?: FileSystemFileHandle) => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        const ok = await loadDocumentGuarded(file);
        if (!ok) {
            return false;
        }

        events.fire('doc.setName', file.name);

        if (handle) {
            documentFileHandle = handle;
            recentFiles.add(handle);
        }
    });

    events.function('doc.open', async () => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        if (fileSelector) {
            fileSelector.show(async (file?: File) => {
                if (file) {
                    const ok = await loadDocumentGuarded(file);
                    if (ok) {
                        events.fire('doc.setName', file.name);
                    }
                }
            });
        } else {
            try {
                const fileHandles = await window.showOpenFilePicker({
                    id: 'NSiteAIDocumentOpen',
                    multiple: false,
                    types: SuperFileType
                });

                if (fileHandles?.length === 1) {
                    const fileHandle = fileHandles[0];

                    // null file handle incase loadDocument fails
                    const file = await fileHandle.getFile();
                    const ok = await loadDocumentGuarded(file);
                    if (!ok) {
                        return;
                    }

                    // store file handle for subsequent saves
                    documentFileHandle = fileHandle;
                    events.fire('doc.setName', fileHandle.name);
                    recentFiles.add(fileHandle);
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        }
    });

    events.function('doc.openRecent', async (fileHandle: FileSystemFileHandle) => {
        if (!events.invoke('scene.empty') && !await getResetConfirmation()) {
            return false;
        }

        try {
            if (await fileHandle.queryPermission({ mode: 'read' }) !== 'granted') {
                if (await fileHandle.requestPermission({ mode: 'read' }) !== 'granted') {
                    return false;
                }
            }

            const ok = await loadDocumentGuarded(await fileHandle.getFile());
            if (!ok) {
                return false;
            }

            // store file handle for subsequent saves
            documentFileHandle = fileHandle;
            events.fire('doc.setName', fileHandle.name);
            recentFiles.add(fileHandle);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error(error);
                await events.invoke('showPopup', {
                    type: 'error',
                    header: localize('popup.error-loading'),
                    message: `${error.message ?? error}`
                });
            }
        }
    });

    events.function('doc.save', async () => {
        if (documentFileHandle) {
            try {
                await saveDocument({
                    stream: await documentFileHandle.createWritable()
                });
                events.fire('doc.saved');
            } catch (error) {
                if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
                    console.error(error);
                }
            }
        } else {
            await events.invoke('doc.saveAs');
        }
    });

    events.function('doc.saveAs', async () => {
        if (window.showSaveFilePicker) {
            try {
                const handle = await window.showSaveFilePicker({
                    id: 'NSiteAIDocumentSave',
                    types: SuperFileType,
                    suggestedName: 'scene.nsproj'
                });
                await saveDocument({ stream: await handle.createWritable() });
                documentFileHandle = handle;
                events.fire('doc.setName', handle.name);
                events.fire('doc.saved');
                recentFiles.add(handle);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            }
        } else {
            await saveDocument({
                filename: 'scene.nsproj'
            });
            events.fire('doc.saved');
        }
    });

    // doc name

    let docName: string = null;

    const setDocName = (name: string) => {
        if (name !== docName) {
            docName = name;
            events.fire('doc.name', docName);
        }
    };

    events.function('doc.name', () => {
        return docName;
    });

    events.on('doc.setName', (name) => {
        setDocName(name);
    });
};

export { registerDocEvents };
